// Build-version watch — detects a new deploy while the app (or installed
// PWA) is already open, and reloads once so the user picks up the new
// build without having to manually refresh or reinstall.
//
// `__APP_BUILD__` is injected at build time (see vite.config.ts `define`)
// and is written alongside it to `dist/version.json`, which is excluded
// from the service-worker precache so it is always fetched fresh from the
// network (see the `globIgnores` entry in vite.config.ts).
declare const __APP_BUILD__: string;

const POLL_INTERVAL_MS = 60_000;
const SESSION_KEY = 'kanvara-build-reloaded';
const INTENDED_KEY = 'kanvara-intended-path';

interface VersionFile {
  build?: unknown;
  builtAt?: unknown;
}

async function fetchLatestBuild(): Promise<string | null> {
  try {
    const res = await fetch('/version.json', { cache: 'no-store' });
    if (!res.ok) return null;
    const data: VersionFile = await res.json();
    return typeof data.build === 'string' ? data.build : null;
  } catch {
    // Offline, network hiccup, or the response wasn't JSON — ignore.
    return null;
  }
}

async function checkForNewBuild(): Promise<void> {
  const latest = await fetchLatestBuild();
  if (!latest || latest === __APP_BUILD__) return;

  // A stale shell may have bounced an unknown path (a route this build does not
  // know, e.g. /cia added in a later deploy) to the home page before this check
  // ran; reload to that path so the new build opens what the link asked for.
  let intended: string | null = null;
  try {
    const raw = sessionStorage.getItem(INTENDED_KEY);
    if (raw) {
      const v = JSON.parse(raw) as { path?: unknown; at?: unknown };
      if (typeof v.path === 'string' && v.path.startsWith('/') && typeof v.at === 'number' && Date.now() - v.at < 5 * 60_000) intended = v.path;
      sessionStorage.removeItem(INTENDED_KEY);
    }
  } catch { /* storage unavailable */ }

  try {
    // Reload at most twice for this exact build in this tab/session (the second
    // attempt covers the race where the old service worker still answered the
    // first navigation) — never loop if the reload didn't pick up the build.
    const seen = sessionStorage.getItem(SESSION_KEY);
    const attempts = seen === latest ? 1 : seen === `${latest}:2` ? 2 : 0;
    if (attempts >= 2) return;
    sessionStorage.setItem(SESSION_KEY, attempts === 0 ? latest : `${latest}:2`);
  } catch {
    // sessionStorage unavailable (private mode, blocked storage, etc.) —
    // fall through and reload anyway, best effort.
  }

  if (intended && intended !== window.location.pathname + window.location.search + window.location.hash) window.location.replace(intended);
  else window.location.reload();
}

/**
 * Starts polling `/version.json` for a build id newer than the one this
 * bundle was built with. Checks every 60s, and immediately whenever the
 * tab regains visibility or focus (the common case for an installed PWA
 * that was left open or backgrounded across a deploy). Reloads the page
 * once when a new build is found. Never throws.
 */
export function startBuildWatch(): void {
  try {
    void checkForNewBuild();

    window.setInterval(() => {
      void checkForNewBuild();
    }, POLL_INTERVAL_MS);

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void checkForNewBuild();
    });

    window.addEventListener('focus', () => {
      void checkForNewBuild();
    });
  } catch {
    // Build-watch is a nice-to-have; never let it break the app.
  }
}

/** A tab opened before a deploy asks for code files the deploy replaced: reload once to get the current build. */
const STALE_CHUNK = /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS|ChunkLoadError/i;
export function reloadIfStaleChunk(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? '');
  if (!STALE_CHUNK.test(msg)) return false;
  try {
    const last = Number(sessionStorage.getItem('kanvara-chunk-reload-at') || 0);
    if (Date.now() - last < 30_000) return false; // just reloaded: don't loop
    sessionStorage.setItem('kanvara-chunk-reload-at', String(Date.now()));
  } catch { /* storage blocked: reload anyway */ }
  window.location.reload();
  return true;
}
